import { z } from 'zod';

export const GUIDANCE_COMPONENTS = [
  'help_bubble',
  'guided_tour',
  'checklist',
  'assisted_field',
  'progress_indicator',
  'warning',
] as const;
export type GuidanceComponent = (typeof GUIDANCE_COMPONENTS)[number];

/** Components that may anchor to the step itself rather than to an element. */
export const STEP_ANCHORABLE_COMPONENTS: readonly GuidanceComponent[] = ['warning', 'progress_indicator'];

export const GUIDANCE_STATES = [
  'proposed',
  'approved',
  'published',
  'disabled',
  'dismissed',
  'deleted',
] as const;
export type GuidanceState = (typeof GUIDANCE_STATES)[number];

/**
 * Multi-strategy anchor: id, label, role+position, rank. First match wins.
 * For a guided tour, `selectors` lists one target per step, in order. An
 * element the measurement could only recognise by its label has no selector:
 * `label` and `elementType` then name the target on their own.
 */
export const anchorSpecSchema = z
  .object({
    selectors: z.array(z.string().max(256)).max(8),
    /** Native type of the target (`file`, `checkbox`, `select-one`), so a label is matched to the right kind of control. */
    elementType: z.string().max(32).nullable().default(null),
    label: z.string().max(256).nullable().default(null),
    role: z.string().max(64).nullable().default(null),
    rank: z.number().int().min(0).nullable().default(null),
  })
  .strict();
export type AnchorSpec = z.infer<typeof anchorSpecSchema>;

/** The six trigger kinds of the functional specification §6.7 (technical §7.4). */
export const TRIGGER_KINDS = [
  'on_step_enter',
  'on_field_focus',
  'on_dwell_time',
  'on_friction_detected',
  'on_significant_interaction',
  'on_user_request',
] as const;
export type TriggerKind = (typeof TRIGGER_KINDS)[number];

/** Friction signals a trigger may wait for. Their thresholds are fixed SDK constants, never here. */
export const FRICTION_SIGNALS = ['hesitation', 'repeated_click', 'dead_click', 'error_state'] as const;
export type FrictionSignal = (typeof FRICTION_SIGNALS)[number];

export const MAX_TRIGGER_CONDITIONS = 3;

export const triggerConditionSchema = z
  .object({
    kind: z.enum(TRIGGER_KINDS),
    /** Restricts the condition to one element of the step; null means the step itself. */
    targetSelector: z.string().max(256).nullable().default(null),
    /** Required by `on_dwell_time`. */
    dwellTimeMs: z.number().int().min(0).max(600_000).nullable().default(null),
    /** Required by `on_friction_detected`. */
    frictionSignal: z.enum(FRICTION_SIGNALS).nullable().default(null),
  })
  .strict()
  .superRefine((condition, context) => {
    if (condition.kind === 'on_dwell_time' && condition.dwellTimeMs === null) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'on_dwell_time requires dwellTimeMs' });
    }
    if (condition.kind === 'on_friction_detected' && condition.frictionSignal === null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'on_friction_detected requires frictionSignal',
      });
    }
  });
export type TriggerCondition = z.infer<typeof triggerConditionSchema>;

/**
 * Composed trigger rule (technical §7.4): one to three conditions combined
 * with AND, plus the non-repetition limits of functional §6.7.
 */
export const triggerRuleSchema = z
  .object({
    conditions: z.array(triggerConditionSchema).min(1).max(MAX_TRIGGER_CONDITIONS),
    /** Default 1: a guidance is shown once per session. */
    maxDisplaysPerSession: z.number().int().min(1).max(10).default(1),
    /** Default 2: at most two components on one step. */
    maxComponentsPerStep: z.number().int().min(1).max(5).default(2),
    /** Delay between the conditions being met and the display. */
    delayMs: z.number().int().min(0).max(30_000).default(0),
  })
  .strict();
export type TriggerRule = z.infer<typeof triggerRuleSchema>;

/** Whether a guidance closed by the visitor stays hidden for the session (functional §6.5). */
export const DISMISSAL_MEMORIES = ['session', 'none'] as const;
export type DismissalMemory = (typeof DISMISSAL_MEMORIES)[number];

export const guidanceContentSchema = z
  .object({
    title: z.string().max(120),
    body: z.string().max(600),
    items: z.array(z.string().max(200)).max(10).default([]),
  })
  .strict();
export type GuidanceContent = z.infer<typeof guidanceContentSchema>;

/** One published guidance, as served to the SDK. */
export const publishedGuidanceSchema = z
  .object({
    id: z.string().uuid(),
    journeyId: z.string().uuid(),
    stepSignatures: z.array(z.string().max(128)).min(1),
    component: z.enum(GUIDANCE_COMPONENTS),
    anchor: anchorSpecSchema.nullable(),
    trigger: triggerRuleSchema,
    dismissalMemory: z.enum(DISMISSAL_MEMORIES).default('session'),
    /** Guided tour only: one decision per step (functional §10.5.1). */
    reinforced: z.boolean().default(false),
    contents: z.record(guidanceContentSchema),
  })
  .strict();
export type PublishedGuidance = z.infer<typeof publishedGuidanceSchema>;
